import { useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/Panel";
import { SelectorModeloDialog } from "@/components/SelectorModeloDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Upload, Trash2, CheckCircle2, AlertTriangle, Plus, Copy } from "lucide-react";

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
const toks = (s) => new Set(norm(s).split(" ").filter(Boolean));

// Extrae año o rango del texto de modelo. Devuelve {y1, y2|null(=actual), rango} o null.
const extraerAnios = (txt) => {
  const rango = (txt || "").match(/\b(19\d{2}|20\d{2})\s*[-–/a\s]+\s*(19\d{2}|20\d{2}|actual|presente|hoy)\b/i);
  if (rango) {
    const y1 = parseInt(rango[1], 10);
    const y2 = /^\d/.test(rango[2]) ? parseInt(rango[2], 10) : null;
    return { y1, y2, rango: true };
  }
  const solo = (txt || "").match(/\b(19\d{2}|20\d{2})\b/);
  if (solo) return { y1: parseInt(solo[1], 10), y2: parseInt(solo[1], 10), rango: false };
  return null;
};

const rangoModelo = (m) => [m.anio_inicio ?? 0, m.anio_fin ?? 9999];

const parseLine = (line) => {
  const out = []; let cur = "", q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
    else if (c === "," && !q) { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur); return out;
};

export const CargaMasivaCatalogo = ({ modelos, onDone, onModelosChange }) => {
  const [items, setItems] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [resumenOpen, setResumenOpen] = useState(false);
  const [selectorFila, setSelectorFila] = useState(null);

  // Detección de modelo CON año: primero candidatos por nombre (nombre más específico),
  // luego se desambigua por el año/rango del texto. Si sigue siendo ambiguo → null ("por elegir").
  const matchModelo = (txt) => {
    const ft = toks(txt);
    let maxLen = 0;
    const cands = [];
    for (const m of modelos) {
      const key = toks(`${m.marca} ${m.modelo}`);
      if (key.size && [...key].every((k) => ft.has(k))) {
        if (key.size > maxLen) maxLen = key.size;
        cands.push({ m, len: key.size });
      }
    }
    const finalistas = cands.filter((c) => c.len === maxLen).map((c) => c.m);
    if (finalistas.length === 0) return null;
    if (finalistas.length === 1) return finalistas[0];   // nombre único → sin ambigüedad
    const info = extraerAnios(txt);
    if (!info) return null;                               // varios candidatos y sin año → por elegir
    if (info.rango) {
      const a1 = info.y1, a2 = info.y2 ?? 9999;
      let best = null, bestOv = 0, empates = 0;
      for (const m of finalistas) {
        const [r1, r2] = rangoModelo(m);
        const ov = Math.min(a2, r2) - Math.max(a1, r1);
        if (ov > bestOv) { bestOv = ov; best = m; empates = 0; }
        else if (ov === bestOv && ov > 0) empates += 1;
      }
      return best && bestOv > 0 && empates === 0 ? best : null;
    }
    const y = info.y1;
    const dentro = finalistas.filter((m) => { const [r1, r2] = rangoModelo(m); return r1 <= y && y <= r2; });
    return dentro.length === 1 ? dentro[0] : null;
  };

  const analizar = async (file) => {
    const texto = await file.text();
    const lineas = texto.split(/\r?\n/).filter((l) => l.trim() !== "");
    const header = parseLine(lineas[0] || "").map((h) => h.trim().toLowerCase());
    const has = (c) => header.includes(c);
    let tipo = null, error = null;
    if (has("operacion") && has("frt_horas")) tipo = "frt";
    else if (has("modelo") && has("grupo") && has("codigo_parte") && has("nombre")) tipo = "catalogo";
    else {
      // No califica: dar un motivo concreto según la intención del archivo.
      const frtSignal = has("operacion") || has("frt_horas") || has("seccion") || has("alcance");
      const catSignal = has("codigo_parte") || has("grupo") || has("es_plastico") || has("nombre");
      if (frtSignal) error = `Faltan columnas: ${["operacion", "frt_horas"].filter((c) => !has(c)).join(", ")}`;
      else if (catSignal) error = `Faltan columnas: ${["modelo", "grupo", "codigo_parte", "nombre"].filter((c) => !has(c)).join(", ")}`;
      else error = "Tipo no detectado (encabezado no reconocido)";
    }
    const filas = Math.max(0, lineas.length - 1);
    if (tipo && filas === 0) error = "0 filas de datos";
    const idx = header.indexOf("modelo");
    let modeloTxt = "";
    if (idx >= 0 && lineas[1]) modeloTxt = (parseLine(lineas[1])[idx] || "").trim();
    const m = (tipo && modeloTxt) ? matchModelo(modeloTxt) : null;
    return { nombre_archivo: file.name, file, tipo, modelo_texto: modeloTxt,
      filas, modelo_id: m ? m.id : "", error };
  };

  const elegir = async (e) => {
    const files = Array.from(e.target.files || []);
    setResumen(null);
    const nuevos = await Promise.all(files.map(analizar));
    setItems((prev) => [...prev, ...nuevos.filter((n) => !prev.some((p) => p.nombre_archivo === n.nombre_archivo))]);
    e.target.value = "";
  };

  const setModelo = (nombre, modelo_id) =>
    setItems((prev) => prev.map((it) => (it.nombre_archivo === nombre ? { ...it, modelo_id } : it)));
  const quitar = (nombre) => setItems((prev) => prev.filter((it) => it.nombre_archivo !== nombre));

  // Una fila es inválida si tiene error de validación o, siendo de tipo válido, no resolvió modelo.
  const motivoInvalido = (it) => it.error || (it.tipo && !it.modelo_id ? "Modelo sin resolver — elegí uno" : null);
  const invalidos = items.filter((it) => motivoInvalido(it));
  const validos = items.filter((it) => it.tipo && !it.error && it.modelo_id);

  const copiarErrores = () => {
    const errs = (resumen?.resumen || []).filter((r) => r.error)
      .map((r) => `${r.nombre_archivo} (${r.tipo || "—"}): ${r.error}`).join("\n");
    navigator.clipboard?.writeText(errs || "Sin errores");
    toast.success("Errores copiados");
  };

  const confirmar = async () => {
    setSubiendo(true);
    try {
      const fd = new FormData();
      const asign = [];
      validos.forEach((it) => {
        fd.append("files", it.file, it.nombre_archivo);
        asign.push({ nombre_archivo: it.nombre_archivo, tipo: it.tipo, modelo_ids: it.modelo_id ? [it.modelo_id] : [] });
      });
      fd.append("asignaciones", JSON.stringify(asign));
      const { data } = await api.post("/catalogo-referencia/importar-lote", fd, { timeout: 180000 });
      setResumen(data);
      setResumenOpen(true);
      const ok = data.resumen.filter((r) => !r.error).length;
      if (data.con_error) {
        toast.warning(`Carga: ${ok} OK, ${data.con_error} con error`, {
          action: { label: "Ver detalle", onClick: () => setResumenOpen(true) },
        });
      } else {
        toast.success(`Carga masiva: ${ok} archivo(s), ${data.total_filas} filas`);
      }
      setItems([]);
      onDone && onDone();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setSubiendo(false); }
  };

  return (
    <Panel titulo="Carga masiva (varios catálogos y FRT a la vez)" testId="carga-masiva-panel">
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Seleccioná varios CSV a la vez (catálogo de piezas y/o FRT). Se detecta el tipo y el modelo de cada archivo.
          Al confirmar, cada modelo que ya tenía catálogo se <b>actualiza</b> (reemplaza, sin duplicar). Nada se carga hasta confirmar.
        </p>
        <input type="file" accept=".csv,text/csv" multiple data-testid="carga-masiva-input" onChange={elegir}
          className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100" />

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="carga-masiva-tabla">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                  <th className="py-2 pr-3 font-semibold">Archivo</th>
                  <th className="py-2 pr-3 font-semibold">Tipo</th>
                  <th className="py-2 pr-3 font-semibold">Modelo</th>
                  <th className="py-2 pr-3 font-semibold">Filas</th>
                  <th className="py-2 text-right font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const motivo = motivoInvalido(it);
                  return (
                  <tr key={it.nombre_archivo} className={`border-b border-slate-100 ${motivo ? "bg-rose-50" : ""}`} data-testid={`carga-masiva-fila-${i}`}>
                    <td className="py-2 pr-3 font-medium text-slate-800">
                      {it.nombre_archivo}
                      {motivo && (
                        <span data-testid={`carga-masiva-motivo-${i}`} className="mt-0.5 flex items-center gap-1 text-xs font-normal text-rose-600">
                          <AlertTriangle size={12} /> {motivo}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {it.tipo === "catalogo" ? <span className="text-indigo-700">Catálogo</span>
                        : it.tipo === "frt" ? <span className="text-amber-700">FRT</span>
                        : <span className="text-rose-600 inline-flex items-center gap-1"><AlertTriangle size={12} /> no detectado</span>}
                    </td>
                    <td className="py-2 pr-3">
                      {(it.tipo === "catalogo" || it.tipo === "frt") ? (
                        <div className="flex items-center gap-1.5">
                          <select data-testid={`carga-masiva-modelo-${i}`} value={it.modelo_id}
                            onChange={(e) => setModelo(it.nombre_archivo, e.target.value)}
                            className={`rounded border px-1.5 py-1 text-xs ${it.modelo_id ? "border-slate-300" : "border-rose-400 bg-rose-50"}`}>
                            <option value="">{it.modelo_texto ? `⚠ ${it.modelo_texto} — por elegir` : "Elegir modelo…"}</option>
                            {modelos.map((m) => <option key={m.id} value={m.id}>{m.nombre_mostrar || `${m.marca} ${m.modelo}`}</option>)}
                          </select>
                          <button type="button" data-testid={`carga-masiva-crear-modelo-${i}`}
                            onClick={() => setSelectorFila(it.nombre_archivo)}
                            className="inline-flex items-center gap-0.5 rounded border border-teal-400 text-teal-700 px-1.5 py-1 text-xs hover:bg-teal-50">
                            <Plus size={12} /> nuevo
                          </button>
                        </div>
                      ) : "—"}
                    </td>
                    <td className={`py-2 pr-3 ${it.tipo && it.filas === 0 ? "text-rose-600 font-semibold" : "text-slate-600"}`}>{it.filas}</td>
                    <td className="py-2 text-right">
                      <button data-testid={`carga-masiva-quitar-${i}`} onClick={() => quitar(it.nombre_archivo)}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {invalidos.length > 0 && (
              <p className="mt-2 text-xs text-rose-600" data-testid="carga-masiva-aviso-invalidos">
                {invalidos.length} archivo(s) con problema se excluirán de la carga (ver motivo en rojo).
              </p>
            )}
            <Button data-testid="carga-masiva-confirmar" onClick={confirmar}
              disabled={subiendo || validos.length === 0}
              className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white">
              <Upload size={15} className="mr-1.5" /> {subiendo ? "Cargando…" : `Confirmar carga (${validos.length} válidos)`}
            </Button>
          </div>
        )}
      </div>

      <Dialog open={resumenOpen} onOpenChange={setResumenOpen}>
        <DialogContent className="max-h-[90vh] flex flex-col" data-testid="carga-masiva-resumen-dialog">
          <DialogHeader>
            <DialogTitle>Resultado de la carga masiva</DialogTitle>
            <DialogDescription>
              {resumen ? `${resumen.resumen.filter((r) => !r.error).length} OK · ${resumen.con_error || 0} con error · ${resumen.total_filas} filas en total` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-xs" data-testid="carga-masiva-resumen-tabla">
              <thead>
                <tr className="border-b border-slate-200 text-left uppercase text-slate-400">
                  <th className="py-1.5 pr-2 font-semibold">Archivo</th>
                  <th className="py-1.5 pr-2 font-semibold">Tipo</th>
                  <th className="py-1.5 pr-2 font-semibold">Modelo</th>
                  <th className="py-1.5 pr-2 font-semibold">Estado</th>
                  <th className="py-1.5 font-semibold">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {(resumen?.resumen || []).map((r, i) => (
                  <tr key={i} className={`border-b border-slate-100 ${r.error ? "bg-rose-50" : ""}`} data-testid={`carga-masiva-res-${i}`}>
                    <td className="py-1.5 pr-2 font-medium text-slate-800">{r.nombre_archivo}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{r.tipo || "—"}</td>
                    <td className="py-1.5 pr-2 text-slate-500">{r.modelo_texto || "—"}</td>
                    <td className="py-1.5 pr-2">
                      {r.error
                        ? <span className="inline-flex items-center gap-1 font-semibold text-rose-600"><AlertTriangle size={12} /> Error</span>
                        : <span className="inline-flex items-center gap-1 font-semibold text-teal-600"><CheckCircle2 size={12} /> OK</span>}
                    </td>
                    <td className="py-1.5 text-slate-600">
                      {r.error ? r.error : `${r.filas} filas · ${r.accion}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter className="shrink-0">
            {resumen?.con_error > 0 && (
              <Button variant="outline" data-testid="carga-masiva-copiar-errores" onClick={copiarErrores}>
                <Copy size={14} className="mr-1.5" /> Copiar errores
              </Button>
            )}
            <Button data-testid="carga-masiva-resumen-cerrar" onClick={() => setResumenOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SelectorModeloDialog abierto={!!selectorFila} onCerrar={() => setSelectorFila(null)}
        modelos={modelos}
        titulo="Buscar o crear modelo"
        descripcion="Elegí un modelo existente o creá uno nuevo para este catálogo."
        onSeleccionar={async (modelo, esNuevo) => {
          if (esNuevo && onModelosChange) await onModelosChange();
          setModelo(selectorFila, modelo.id);
          setSelectorFila(null);
        }} />
    </Panel>
  );
};
